/**
 * MOCK/TEMPORARY — cover artwork.
 *
 * ============================================================================
 * NO IMAGE IS UPLOADED AND NO IMAGE IS GENERATED. `uploadCover` hands back the
 * data URL the browser already read from the user's disk, and `generateCover`
 * returns a seed for the deterministic gradient stand-in — plus the exact prompt
 * an image service would have been sent, which is the half of generation that IS
 * finished.
 *
 * The distinction matters for what happens next: the upload path already carries
 * real pixels the user chose, so it becomes real the moment there is somewhere to
 * PUT them. The generation path has no image at all and stays simulated until an
 * image model exists behind it. `describeCoverBlockers()` says so in a form the UI
 * renders rather than restates.
 * ============================================================================
 *
 * WHAT CHANGED, AND WHY IT IS NOT COSMETIC. `generateCover` used to take `style` and
 * `prompt` and discard both, returning `newId()` as the seed — so the cover was decided
 * by a fresh UUID and had no relationship to the song. Every automatically generated song
 * was worse still: `generationApi.createGeneration` set no artwork field at all, so the
 * gradient fell back to the song's id (lib/artwork.artworkSeedFor). Two songs from the
 * same prompt looked different, and the prompt, the genre and the mood influenced nothing.
 * The direction now comes from lib/coverConcept, which reads all three; the seed is a hash
 * of that direction. The gradient is still a gradient — that part cannot be fixed from the
 * client — but it is now derived from the song rather than from an identifier.
 *
 * Real endpoints this stands in for:
 *
 *   POST /songs/:id/cover        multipart/form-data, returns { coverUrl }
 *     Uploads the user's file and returns its hosted URL. MUST re-validate type
 *     and size server-side — lib/imageUpload.ts's checks run in the browser and
 *     are advisory only. Should also strip EXIF and re-encode, which a client
 *     cannot be trusted to have done.
 *
 *   POST /songs/:id/cover/generate   { style, prompt, imagePrompt }, returns { coverUrl }
 *     Runs the image model and returns the result. `imagePrompt` is the composed
 *     instruction from lib/coverConcept — the backend is free to send it as-is or to
 *     recompose it from the fields, and it is included precisely so the frontend's
 *     reading of the request is inspectable rather than implied. When this is real, the
 *     `artworkSeed` field disappears from the response and `coverUrl` carries the image —
 *     which is why every surface reads artwork through lib/artwork rather than reaching
 *     for the seed itself.
 */
import { API_BASE_URL, USE_MOCK_API } from '../config';
import { coverConceptFor } from '../lib/coverConcept';
import type { CoverConcept } from '../lib/coverConcept';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

/** The result of applying artwork, in the shape a real endpoint would return. */
export interface CoverResult {
  /**
   * The hosted image URL. A data URL on the upload path today; null on the
   * generation path, because no image exists to point at.
   */
  coverUrl: string | null;
  /**
   * Seed for the gradient stand-in. Present only while generation is simulated —
   * a real image service returns a coverUrl and no seed.
   */
  artworkSeed?: string;
  /**
   * The visual direction the seed was derived from.
   *
   * Returned, and not merely used internally, because it is the evidence: a caller (and a
   * test, and a reviewer) can see which concept was read out of the prompt and what
   * instruction an image model would have been given. A real endpoint would drop this
   * along with `artworkSeed`.
   */
  concept?: CoverConcept;
}

/**
 * One reason no cover image can actually be generated.
 *
 * Same record shape as api/lyria.LyriaBlocker and generationApi.MusicDnaBlocker, and
 * deliberately so: the UI already knows how to render a list of `{ summary, resolution }`
 * and a third shape for the same idea would be a third thing to keep in step.
 */
export interface CoverBlocker {
  /** Short identifier, for tests and for keys. */
  id: 'no-backend' | 'no-image-model';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/** What the cover-generation path can and cannot do, for the UI to read. */
export interface CoverSupport {
  /** True when a request could actually produce an image file. False today. */
  available: boolean;
  /** Why `available` is false, in the order it would have to be fixed. */
  blockers: CoverBlocker[];
}

/**
 * Everything standing between a cover request and an actual image.
 *
 * Computed rather than constant, like describeLyriaBlockers(), so the first entry
 * disappears on its own the day `VITE_API_BASE_URL` is set instead of needing an edit
 * here and another in the UI.
 *
 * THE SECOND BLOCKER IS THE REAL ONE and it does not clear with configuration. Lyria 3
 * generates music, not pictures (api/lyria.ts), so cover art needs a different service
 * chosen and paid for — and whichever one it is, the same rule applies as to Google's
 * music API: its key belongs to the backend, because a key given to a Vite build is
 * inlined into the bundle and served to every visitor.
 */
export function describeCoverBlockers(): CoverBlocker[] {
  const blockers: CoverBlocker[] = [];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary: 'No Lyrixsa backend is configured, so there is nothing to send the request to.',
      resolution:
        'Set VITE_API_BASE_URL and implement POST /songs/:id/cover/generate server-side, ' +
        'forwarding the instruction this app composes.',
    });
  }

  blockers.push({
    id: 'no-image-model',
    summary: 'No image generation model is configured for cover art yet.',
    resolution:
      'Choose a server-side image generation service and have POST /songs/:id/cover/generate ' +
      'call it with server-held credentials.',
  });

  return blockers;
}

/** What a caller needs to know before it offers cover generation. Mirrors audioUploadApi. */
export function coverSupport(): CoverSupport {
  const blockers = describeCoverBlockers();
  return { available: blockers.length === 0, blockers };
}

/**
 * What a generation request carries. Exported so a test can assert the exact body.
 *
 * Pure data, and built by `buildCoverRequest` below rather than inside the method, for
 * the reason generationApi.buildImageMusicRequest gives: being able to assert the shape
 * without a server is what distinguishes a real boundary from an intention.
 */
export interface CoverRequest {
  songId: string;
  /** The style the artwork should be in — from COVER_STYLES. */
  style: string;
  /** What the song is about, in the user's words. May be empty. */
  prompt: string;
  /** The composed instruction. See lib/coverConcept.CoverConcept.imagePrompt. */
  imagePrompt: string;
}

/** Everything the cover for one song is derived from. */
export interface CoverGenerationInput {
  songId: string;
  /** The song's own prompt — where the subject comes from. */
  prompt?: string | null;
  /** The song's genre — where the style comes from when the user names none. */
  genre?: string | null;
  /** The song's mood — where the light and colour come from. */
  mood?: string | null;
  /** A style the user picked in the Cover Art panel, overriding the genre's. */
  style?: string | null;
  /** Passed by "Regenerate" to get a different take. See CoverConceptInput.variant. */
  variant?: string | number | null;
}

/** Builds the documented request body for a cover generation. */
export function buildCoverRequest(input: CoverGenerationInput): CoverRequest {
  const concept = coverConceptFor(input);
  return {
    songId: input.songId,
    style: concept.style,
    prompt: input.prompt?.trim() ?? '',
    imagePrompt: concept.imagePrompt,
  };
}

export const coverApi = {
  /**
   * MOCK: POST /songs/:id/cover
   *
   * Echoes the data URL back after a short delay. The delay is not decoration: it
   * is what makes the applying state in the UI real rather than a branch that never
   * renders, and a genuine upload of an 8MB file will be considerably slower.
   *
   * `songId` is unused by the mock and named for the endpoint it becomes, so the
   * call site already reads the way it will read against a server.
   */
  async uploadCover(songId: string, dataUrl: string): Promise<CoverResult> {
    if (!USE_MOCK_API) {
      return http.post<CoverResult>(`/songs/${songId}/cover`, { dataUrl });
    }
    assertMock('coverApi.uploadCover');
    return mockDelay({ coverUrl: dataUrl });
  },

  /** What cover generation can do right now. Same shape as audioUploadApi.support(). */
  support(): CoverSupport {
    return coverSupport();
  },

  /** The request body a generation would send. Exposed so it is testable and inspectable. */
  buildRequest(input: CoverGenerationInput): CoverRequest {
    return buildCoverRequest(input);
  },

  /**
   * MOCK: POST /songs/:id/cover/generate
   *
   * Returns a seed and NO image, and says which concept it read. `coverUrl` is null and
   * stays null: there is no image model, so resolving with a URL — of any kind — would be
   * claiming an image exists. The caller stages the result and the caption reads
   * "Placeholder artwork" (lib/artwork.coverSourceLabel), which is the truth.
   *
   * The arguments are USED. They were accepted and discarded before this batch, which is
   * what made every generated cover a fresh UUID's gradient; the seed is now a hash of the
   * style, the genre, the mood and the strongest visual concept in the prompt, so the same
   * direction always renders the same artwork and a different one does not.
   *
   * The body a real call would post is `buildCoverRequest(input)` — built from the same
   * concept, exported above, and asserted in the tests, so wiring the endpoint is a change
   * to this function rather than to the panel that calls it.
   */
  async generateCover(input: CoverGenerationInput): Promise<CoverResult> {
    if (!USE_MOCK_API) {
      const request = buildCoverRequest(input);
      return http.post<CoverResult>(`/songs/${input.songId}/cover/generate`, request);
    }
    assertMock('coverApi.generateCover');
    const concept = coverConceptFor(input);
    return mockDelay({ coverUrl: null, artworkSeed: concept.seed, concept });
  },
};
